import { Box, Button, Grid } from "@mui/material";
import {
  ConfirmFormModal,
  DrawStage,
  isRole,
  SnackBarMessage
} from "@toorak/tc-common-fe-sdk";
import React, { Suspense, useEffect, useRef, useState } from "react";
import HistoryIcon from "@mui/icons-material/HistoryOutlined";
import { useDispatch, useSelector, shallowEqual } from "react-redux";

import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { styled } from "@mui/material/styles";
import {
  hideLoader,
  showLoader
} from "../../../stores/loaderAndException/loaderAndException.action";
import { countDrawErrors } from "../DrawErrorCheck";
import {
  updateDrawDetails,
  postDrawStageHistory
} from "../../../stores/drawRequest/drawRequest.action";
import { StageAuditTrail } from "./StageAuditTrail";
import { DrawRolesEnum, showBudgetReconcile } from "../../../utils/constants";
import { RootState } from "../../../stores/rootReducer";
import { getRoles, isAMTView } from "../../AssetManagement.utils";
import {
  DrawRequestStore,
  drawsActionBtnInterface
} from "../../../stores/drawRequest/drawRequest.interface";
import { useDrawsStyles } from "../Draws.styles";
import {
  DontShowHistoryStages,
  OriginatorEditingStages,
  getStageBgColor,
  snackBarDetails,
  getColorBasedOnStatus
} from "../Constant";
import { DrawStageUpdatePopup } from "./DrawStageUpdatePopup";
import { updateDrawStage } from "../../../stores/drawRequest/drawRequest.reducer";
import { DrawDocsButtonsWrapper } from "../DrawDocTagging/DrawDocsButtonsWrapper";
import { getTasks, taskNames } from "../../../stores/tasks/Tasks.action";
import { budgetReviewStatusEnum } from "../../../ilp/list-view/BudgetTaskManagement";
import { HtmlWhiteTooltip } from "../../../utils/Tooltip";

const StyledInfoIcon = styled(InfoOutlinedIcon)({
  width: 15,
  height: 15,
  marginLeft: 5,
  marginTop: 2,
  color: "#32325d"
});

export enum popUpHeaderEnum {
  BudgetReviewConfirmation = "Budget Review Confirmation",
  UnSupervisedBudgetReview = "Unsupervised Budget",
  PrimaryBudgetNotAvailable = "Primary Budget Document not available",
  PreviousDrawReconcilePending = "Previous Draw Reconcile Pending",
  DrawWired = "Draw Wired"
}

const DrawMoreAction = React.lazy(() =>
  import("./DrawMoreAction").then((module) => ({
    default: module.DrawMoreAction
  }))
);

interface DrawDetailsHeaderInterface {
  drawId: string;
  isOrig: boolean;
  showDeleteDrawWarning: () => void;
  updateIsSubmitClicked: (val: boolean) => void;
  goBackToLoanListPage: () => void;
  updateIsEditing: (val: boolean) => void;
  isEditing: boolean;
  toorakLoanId: string;
}

export const DrawDetailsHeader = ({
  drawId,
  isOrig,
  showDeleteDrawWarning,
  updateIsSubmitClicked,
  goBackToLoanListPage,
  updateIsEditing,
  isEditing,
  toorakLoanId
}: DrawDetailsHeaderInterface) => {
  const { drawDetails, drawDocs, selectedBankDetails } = useSelector<
    RootState,
    DrawRequestStore
  >((state) => state.drawRequestStore, shallowEqual);
  const classes = useDrawsStyles();
  const dispatch = useDispatch();
  const [snackBarMsg, setSnackBarMsg] = useState<{
    messageType: "success" | "info" | "warning" | "error" | undefined;
    message: string;
  }>({
    messageType: undefined,
    message: ""
  });
  const [budgetReviewStatus, setBudgetReviewStatus] = useState<string>("");
  const [allDrawDocs, setAllDrawDocs] = useState<any>([]);
  const [budgetReconDisable, setBudgetReconDisable] = useState<boolean>(false);
  const [popUpInfo, setPopUpInfo] = useState<any>({
    popUpOpen: false,
    popUpBody: "",
    primaryBtnName: "",
    SecondaryBtnName: "",
    hidePrimaryBtn: false,
    hideSecondaryBtn: false,
    header: ""
  });
  const [showStatusUpdate, setShowStatusUpdate] = useState<boolean>(false);
  const [stageToUpdate, setStageToUpdate] = useState<DrawStage | null>(null);
  const [showStageHistory, setShowStageHistory] = useState<boolean>(false);
  const [showStageHistoryIcon, setShowStageHistoryIcon] =
    useState<boolean>(false);
  const lastBudgetDocsKeyRef = useRef<string>("");
  const { tasks } = useSelector<RootState, any>((state) => state.TasksStore);

  useEffect(() => {
    if (!toorakLoanId) return;
    if (!tasks?.length) {
      getTasks(dispatch, {
        resourceTypes: ["LOAN"],
        resourceIds: [toorakLoanId],
        taskNames: [
          taskNames.TAG,
          taskNames.SPLIT,
          taskNames.REVIEW,
          taskNames.RECONCILE,
          taskNames.BUDGET_REVIEW,
          taskNames.BUDGET_APPROVE
        ]
      });
    }
  }, [toorakLoanId, tasks?.length]);

  useEffect(() => {
    if (!drawDocs?.length) return;
    // Wait for tasks to load before proceeding
    if (!tasks?.length) {
      setBudgetReconDisable(true);
      return;
    }
    const budgetReviewStatus = tasks?.[0]?.assignedTasks.find((task: any) =>
      [taskNames.BUDGET_REVIEW].includes(task.taskName)
    )?.taskStatus;
    const filDocsList = drawDocs.filter(
      (item: any) =>
        item.isPurchasePrimary &&
        item.tags.some(
          (tag: any) => tag.tag.code === "DDDC" || tag.tag.code === "ADRF"
        )
    );
    const docsKey = `${budgetReviewStatus ?? ""}|${filDocsList.length}`;
    if (lastBudgetDocsKeyRef.current === docsKey) return;
    lastBudgetDocsKeyRef.current = docsKey;
    setBudgetReviewStatus(budgetReviewStatus ?? "");
    setAllDrawDocs(filDocsList);
    setBudgetReconDisable(!filDocsList.length);
  }, [drawDocs, tasks?.length]);

  const handleBudgeReconcileClick = (tabRedirect: any) => {
    const filteredDocsList = allDrawDocs.filter(
      (item: any) =>
        item.isPurchasePrimary &&
        item.tags.some(
          (tag: any) => tag.tag.code === "DDDC" || tag.tag.code === "ADRF"
        )
    );
    if (
      budgetReviewStatus === budgetReviewStatusEnum.Approved &&
      filteredDocsList.length
    ) {
      tabRedirect("budgetReconciliation");
      return;
    }
    if (
      [
        budgetReviewStatusEnum.LoanSubmitted,
        budgetReviewStatusEnum.BudgetMissing
      ].includes(budgetReviewStatus as budgetReviewStatusEnum)
    ) {
      setPopUpInfo({
        popUpOpen: true,
        popUpBody: (
          <div style={{ paddingLeft: "21px", paddingRight: "40px" }}>
            Primary Budget Review Document not detected on the loan. Please mark
            a primary "Scope of Work" document for the loan.
          </div>
        ),
        hidePrimaryBtn: false,
        hideSecondaryBtn: false,
        SecondaryBtnName: "Cancel",
        primaryBtnName: "Go to Document Tagging",
        header: popUpHeaderEnum.PrimaryBudgetNotAvailable
      });
      return;
    }
    if (
      ![budgetReviewStatusEnum.Approved].includes(
        budgetReviewStatus as budgetReviewStatusEnum
      )
    ) {
      setPopUpInfo({
        popUpOpen: true,
        popUpBody: (
          <div style={{ paddingLeft: "21px", paddingRight: "40px" }}>
            Budget Review needs to be done first on the loan, do you want to
            proceed with Budget Review?
          </div>
        ),
        hidePrimaryBtn: false,
        hideSecondaryBtn: false,
        SecondaryBtnName: "Cancel",
        primaryBtnName: "Go to Budget Review",
        header: popUpHeaderEnum.BudgetReviewConfirmation
      });
      return;
    }
    tabRedirect("budgetReconciliation");
  };

  useEffect(() => {
    setShowStageHistoryIcon(
      !DontShowHistoryStages.includes(drawDetails?.stage)
    );
  }, [drawDetails?.stage]);

  const role: DrawRolesEnum = getRoles();

  const {
    stage,
    fpiFlag,
    inspectionReviewStatus,
    budgetReconcileStatus,
    documentTaggingStatus,
    repeatBorrower
  } = drawDetails;
  const compactChipStyle = {
    padding: "1px 7px 1px",
    fontSize: "10px",
    lineHeight: 1.1
  } as const;

  const [openSnackBar, setOpenSnackBar] = useState<boolean>(false);
  const {
    draft,
    drawSubmitted,
    initialApproval,
    finalApproval,
    decline,
    wireTransferInitiated,
    WireUnsuccessful,
    wireCompleted,
    DrawInQueue,
    wireStatusReview
  } = DrawStage;

  const handleSaveClick = async () => {
    if (stage !== draft) {
      const errors = countDrawErrors(drawDetails, selectedBankDetails);
      if (errors) {
        updateIsSubmitClicked(true);
        const routing = String(selectedBankDetails?.routing ?? "");
        const message =
          routing && routing.length !== 9
            ? "Routing number must be of 9 digits"
            : "Please fill all required fields before saving.";
        setSnackBarMsg({ messageType: "warning", message });
        setOpenSnackBar(true);
        return;
      }
    }
    const isEditingStage = [
      ...OriginatorEditingStages,
      draft,
      initialApproval,
      finalApproval,
      decline,
      WireUnsuccessful,
      wireStatusReview
    ].includes(drawDetails.stage);

    if (!isEditingStage) {
      return;
    }

    const updateStage: DrawStage = drawDetails.stage;
    try {
      await dispatch(
        updateDrawDetails(
          drawDetails,
          selectedBankDetails,
          drawId,
          isOrig,
          updateStage,
          drawDetails.originatorId
        )
      );
      updateIsEditing(false);
      setSnackBarMsg({
        messageType: "success",
        message: "Draw is successfully saved."
      });
      setOpenSnackBar(true);
    } catch (error) {
      console.error("Error saving draw details:", error);
      setSnackBarMsg({
        messageType: "error",
        message: "Draw is not saved."
      });
      setOpenSnackBar(true);
    }
  };

  const handleSubmitClick = async () => {
    const errors = countDrawErrors(drawDetails, selectedBankDetails);
    Object.keys(selectedBankDetails).forEach((field) => {
      if (field === "routing" && selectedBankDetails[field]?.length != 9) {
        setSnackBarMsg({
          messageType: "warning",
          message: "Routing number must be of 9 digits"
        });
        setOpenSnackBar(true);
      }
    });
    if (errors) {
      console.error("field Errors count:", errors);
      updateIsSubmitClicked(true);
      return;
    }
    updateIsSubmitClicked(false);

    dispatch(showLoader());

    await dispatch(
      updateDrawDetails(
        drawDetails,
        selectedBankDetails,
        drawId,
        isOrig,
        drawSubmitted,
        drawDetails.originatorId
      )
    );
    dispatch(hideLoader());
    setSnackBarMsg({
      messageType: "success",
      message: "Draw is successfully submitted."
    });
    setOpenSnackBar(true);
  };

  const showSubmitButton = () =>
    stage?.toUpperCase() === "DRAFT" &&
    !isRole(DrawRolesEnum.ORIGINATOR_VIEW_DRAW);
  const showEditDrawButton = () =>
    isOrig
      ? OriginatorEditingStages.includes(stage) &&
        !isRole(DrawRolesEnum.ORIGINATOR_VIEW_DRAW)
      : ![draft, wireTransferInitiated, wireCompleted].includes(stage) &&
        isAMTView();
  const showDeleteDrawButton = () =>
    [drawSubmitted, DrawInQueue, draft].includes(stage) &&
    !isRole(DrawRolesEnum.ORIGINATOR_VIEW_DRAW);
  const showDrawMoreAction = () => {
    if (!showEditDrawButton()) {
      return true;
    }
    if (isEditing) {
      return false;
    }
    return true;
  };

  const handleStageUpdateSuccess = (drawstage: DrawStage) => {
    dispatch(updateDrawStage(drawstage));
    setSnackBarMsg({
      messageType: "success",
      message: "Draw Status is successfully updated."
    });
    setOpenSnackBar(true);
  };

  const handleActionBtnClick = async (draw: drawsActionBtnInterface) => {
    if (
      [decline, wireTransferInitiated, wireStatusReview].includes(
        draw.title as DrawStage
      ) ||
      [wireTransferInitiated, wireCompleted].includes(stage)
    ) {
      // changing the status from wireTransferInitiated, wireCompleted to any other status OR
      // changing the status to decline, wireTransferInitiated
      // show updateComments pop up
      setStageToUpdate(draw.title as DrawStage);
      setShowStatusUpdate(true);
    } else {
      try {
        await dispatch(
          postDrawStageHistory(
            drawId,
            draw.title as DrawStage,
            role,
            drawDetails.originatorId
          )
        );
        handleStageUpdateSuccess(draw.title as DrawStage);
      } catch (error) {
        console.error("Error updating draw stage:", error);
        setSnackBarMsg({
          messageType: "error",
          message: "Draw Status is not updated."
        });
        setOpenSnackBar(true);
      }
    }
  };

  const handleConfirmStageUpdate = async (updateComments: string) => {
    if (!stageToUpdate) return;
    try {
      await dispatch(
        postDrawStageHistory(
          drawId,
          stageToUpdate,
          role,
          drawDetails.originatorId,
          updateComments
        )
      );
      handleStageUpdateSuccess(stageToUpdate);
    } catch (error) {
      console.error("Error confirming draw stage update:", error);
      setSnackBarMsg({
        messageType: "error",
        message: "Draw Status is not updated."
      });
      setOpenSnackBar(true);
    }
    setShowStatusUpdate(false);
  };

  const successHandler = () => {
    const { header } = popUpInfo;
    const budgetReconciliationUrl = `/drawDetails/${toorakLoanId}/${drawId}/budgetReconciliation`;
    const redirectUrlByHeader: Partial<Record<string, string>> = {
      [popUpHeaderEnum.BudgetReviewConfirmation]: `/internal/loan/createLoan/BridgeLoan/POST/${toorakLoanId}/budgetReview/v2`,
      [popUpHeaderEnum.PrimaryBudgetNotAvailable]: `/internal/loan/createLoan/BridgeLoan/POST/${toorakLoanId}/documentReview?view=tag`,
      [popUpHeaderEnum.UnSupervisedBudgetReview]: budgetReconciliationUrl,
      [popUpHeaderEnum.PreviousDrawReconcilePending]: budgetReconciliationUrl,
      [popUpHeaderEnum.DrawWired]: budgetReconciliationUrl
    };
    const redirectUrl = redirectUrlByHeader[header];
    if (redirectUrl) {
      window.open(redirectUrl, "_blank");
    }

    setPopUpInfo({
      popUpOpen: false,
      popUpBody: "",
      primaryBtnName: "",
      SecondaryBtnName: "",
      hidePrimaryBtn: false,
      hideSecondaryBtn: false,
      header: ""
    });
  };

  return (
    <Grid item container xs={12} className={classes.headerContainer}>
      {isAMTView() && (
        <DrawStageUpdatePopup
          showStatusUpdate={showStatusUpdate}
          setShowStatusUpdate={setShowStatusUpdate}
          handleConfirmStageUpdate={handleConfirmStageUpdate}
        />
      )}
      <Suspense fallback={<span>Loading...</span>}>
        <ConfirmFormModal
          isOpen={popUpInfo.popUpOpen}
          handleClose={() => {
            setPopUpInfo({
              popUpOpen: false,
              popUpBody: "",
              primaryBtnName: "",
              SecondaryBtnName: "",
              hidePrimaryBtn: false,
              hideSecondaryBtn: false,
              header: ""
            });
          }}
          header={popUpInfo.header}
          body={popUpInfo.popUpBody}
          confirmCondition={false}
          primaryBtnName={popUpInfo.primaryBtnName}
          SecondaryBtnName={popUpInfo.SecondaryBtnName}
          hidePrimaryBtn={popUpInfo.hidePrimaryBtn}
          hideSecondaryBtn={popUpInfo.hideSecondaryBtn}
          size="lg"
          style={{ width: "670px !important" }}
          successHandler={successHandler}
          disableBackdropClick
        />
      </Suspense>
      <Grid
        item
        container
        style={{ width: "66%", minWidth: "802px", alignItems: "flex-start" }}
      >
        <span className={classes.drawHeaderBadgesWrap}>
          <Box
            className="button-typography-wrapper-left"
            style={{ padding: "0px" }}
          >
            <Button
              variant="contained"
              data-testid="back-button"
              color="primary"
              startIcon={<KeyboardArrowLeftIcon />}
              onClick={goBackToLoanListPage}
            >
              Back
            </Button>
          </Box>
          <span className={classes.itemHeaderText}>
            Draw Request ID: {drawId}
          </span>
          {/* change stage color here */}
          <span
            className={classes.stageDisplay}
            data-testid="draw-view-stageDisplay"
            style={{ backgroundColor: getStageBgColor(stage) }}
          >
            {stage?.toUpperCase()}
          </span>
          <span>
            {showStageHistoryIcon && (
              <button
                type="button"
                data-testid="draw-view-loanHistoryBtn"
                aria-label="View draw stage history"
                aria-expanded={showStageHistory}
                style={{
                  position: "relative",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  marginLeft: "-6px",
                  padding: "2px",
                  border: "none",
                  background: "transparent",
                  cursor: "pointer",
                  borderRadius: "50%"
                }}
                onMouseEnter={() => setShowStageHistory(true)}
                onMouseLeave={() => setShowStageHistory(false)}
                onFocus={() => setShowStageHistory(true)}
                onBlur={() => setShowStageHistory(false)}
              >
                <HistoryIcon fontSize="small" />
                {showStageHistory && <StageAuditTrail />}
              </button>
            )}
          </span>
          {!isOrig && (
            <span
              style={{
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                columnGap: "12px",
                rowGap: "6px",
                flexBasis: "100%",
                marginLeft: "106px",
                width: "calc(100% - 106px)"
              }}
            >
              {!!toorakLoanId && repeatBorrower === "Yes" && (
                <span
                  className={classes.stageDisplay}
                  style={{
                    backgroundColor: "#5e72e4",
                    width: "auto"
                  }}
                >
                  REPEAT BORROWER
                </span>
              )}
              {!!toorakLoanId && (
                <span
                  className={classes.stageDisplay}
                  data-testid="draw-view-fpiFlagDisplay"
                  style={{
                    backgroundColor: "#f3c316",
                    color: "#172b4d"
                  }}
                >
                  {fpiFlag || "Non FPI"}
                </span>
              )}
              {showBudgetReconcile(stage) && (
                <>
                  <span
                    className={classes.stageDisplay}
                    data-testid="draw-view-stageDisplay"
                    style={{
                      backgroundColor: getColorBasedOnStatus(
                        documentTaggingStatus
                      ),
                      ...compactChipStyle
                    }}
                  >
                    Document Tagging
                    <HtmlWhiteTooltip
                      title={documentTaggingStatus?.toUpperCase()}
                    >
                      <StyledInfoIcon
                        style={{ verticalAlign: "middle", marginLeft: "5px" }}
                      />
                    </HtmlWhiteTooltip>
                  </span>
                  <span
                    className={classes.stageDisplay}
                    data-testid="draw-view-stageDisplay"
                    style={{
                      backgroundColor: getColorBasedOnStatus(
                        budgetReconcileStatus
                      ),
                      ...compactChipStyle
                    }}
                  >
                    Budget Reconcile
                    <HtmlWhiteTooltip
                      title={budgetReconcileStatus?.toUpperCase()}
                    >
                      <StyledInfoIcon
                        style={{ verticalAlign: "middle", marginLeft: "5px" }}
                      />
                    </HtmlWhiteTooltip>
                  </span>
                  <span
                    className={classes.stageDisplay}
                    data-testid="draw-view-stageDisplay"
                    style={{
                      backgroundColor: getColorBasedOnStatus(
                        inspectionReviewStatus
                      ),
                      ...compactChipStyle
                    }}
                  >
                    Inspection Review
                    <HtmlWhiteTooltip
                      title={inspectionReviewStatus?.toUpperCase()}
                    >
                      <StyledInfoIcon
                        style={{ verticalAlign: "middle", marginLeft: "5px" }}
                      />
                    </HtmlWhiteTooltip>
                  </span>
                </>
              )}
            </span>
          )}
        </span>
      </Grid>
      <Grid
        item
        container
        style={{
          justifyContent: "flex-end",
          width: "34%",
          minWidth: "555px",
          position: "absolute",
          right: 5
        }}
      >
        {showEditDrawButton() && (
          <>
            {isEditing ? (
              <Button
                variant="outlined"
                onClick={handleSaveClick}
                data-testid="save-button"
                className={classes.saveButton}
              >
                Save
              </Button>
            ) : (
              <Button
                onClick={() => {
                  updateIsEditing(true);
                }}
                data-testid="edit-button"
                className={classes.editButton}
              >
                Edit
              </Button>
            )}
          </>
        )}
        {showDeleteDrawButton() ? (
          <Button
            variant="text"
            data-testid="draw-delete-btn"
            className={`${classes.cancelButton} ${
              stage?.toUpperCase() === "DRAFT"
                ? classes.cancelButton
                : classes.deleteDrawButton
            }`}
            onClick={() => showDeleteDrawWarning()}
          >
            Delete Draw
          </Button>
        ) : null}
        {showSubmitButton() && (
          <span>
            <Button
              variant="outlined"
              onClick={handleSaveClick}
              data-testid="save-button"
              className={classes.saveButton}
            >
              {" "}
              Save{" "}
            </Button>
            <Button
              variant="contained"
              onClick={handleSubmitClick}
              data-testid="submit-button"
              className={classes.submitButton}
              color="info"
            >
              Submit
            </Button>
          </span>
        )}
        {isAMTView() && showBudgetReconcile(stage) && (
          <DrawDocsButtonsWrapper
            drawId={drawId}
            loanId={toorakLoanId}
            disable={!drawDocs?.length}
            handleBudgeReconcileClick={handleBudgeReconcileClick}
            budgetReconDisable={budgetReconDisable}
            dispatch={dispatch}
            isOrig={isOrig}
            setSnackBarMsg={setSnackBarMsg}
            setOpenSnackBar={setOpenSnackBar}
          />
        )}
        {isAMTView() && showDrawMoreAction() && (
            <Suspense fallback={<span>Loading...</span>}>
              <DrawMoreAction
                handleActionBtnClick={handleActionBtnClick}
                drawDetails={drawDetails}
              />
            </Suspense>
          )}
      </Grid>
      <SnackBarMessage
        open={openSnackBar}
        verticalPlacement={snackBarDetails.verticalPlacement}
        horizontalPlacement={snackBarDetails.horizontalPlacement}
        autoHideDuration={snackBarDetails.autoHideDuration}
        handleClose={() => setOpenSnackBar(false)}
        messageType={snackBarMsg.messageType}
        message={snackBarMsg.message}
        data-testid="draw-update-message"
      />
    </Grid>
  );
};